import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  theme,
  Tooltip,
  Skeleton,
  message
} from 'antd'
import React, { useState } from 'react'
import CustomRow from '../../../../reusable-components/CustomRow'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAdd,
  faCheckCircle,
  faEye,
  faRefresh,
  faSearch,
  faFileInvoiceDollar,
  faTrash
} from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { useDispatch } from 'react-redux'
import coreReducer from '../../../../../core/utils/reducer'
import CoreEnums from '../../../../../core/utils/enums'
import RemittanceForm from './RemittanceForm'
import { useMedicalAidPaymentList } from '../hooks'

import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import MedicalAidPaymentForm from '../../medical-aid-payment-form'
import AgiliteSkeleton from '../../../../reusable-components/AgiliteSkeleton'
import { ContainerCard } from '../../../../reusable-components/AgiliteCards'
import PageHeader from '../../../../reusable-components/PageHeader'

// Add spin animation with more refined keyframes
const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(360deg); }
  }
  
  .ant-table-row:hover .service-icon {
    transform: scale(1.1);
    transition: all 0.2s ease;
  }
  
  .service-icon {
    transition: all 0.2s ease;
  }
  
  .ant-table-row {
    transition: all 0.3s ease;
  }
  
  .ant-table-row:hover {
    background-color: rgba(24, 144, 255, 0.05) !important;
  }

  .ant-table-thead th h2 {
    font-size: 15px !important;
    margin: 0;
  }
  
  .skeleton-cell {
    padding: 0 !important;
  }
  
  .skeleton-tag {
    display: flex;
    justify-content: center;
  }
  
  .skeleton-action {
    display: flex;
    justify-content: center;
    gap: 6px;
  }
`

/**
 * Component for displaying and managing remittance list
 */
const RemittanceList = () => {
  const dispatch = useDispatch()
  const { token } = theme.useToken()
  const [remittanceForm] = Form.useForm()

  // Initialize hook for medical aid payment list
  const {
    loading,
    data,
    page,
    pageSize,
    total,
    filters,
    remittanceLoading,
    remittanceModalVisible,
    schemeData,
    schemeLoading,
    setPage,
    setPageSize,
    setFilters,
    setRemittanceModalVisible,
    setSchemeData,
    handleGetRemittances,
    handleFinalizeRemittance,
    handleSaveRemittance,
    debouncedSchemeFilter
  } = useMedicalAidPaymentList()

  /**
   * Opens remittance details in a new tab
   * @param {Object} record - Remittance record to open
   */
  const handleOpenRemittance = (record) => {
    dispatch(
      coreReducer.actions.addTab({
        key: `${CoreEnums.tabKeys.REMITTANCES}_${record._id}`,
        closable: true,
        label: `Remittance: ${dayjs(record.createdAt).format('YYYY-MM-DD')}`,
        children: <RemittanceForm data={record} isNew={false} handleGetRemittances={handleGetRemittances} />
      })
    )
  }

  /**
   * Saves a new remittance and opens it in the payment form
   */
  const handleSaveAndOpenPaymentForm = async () => {
    await remittanceForm.validateFields()

    const remittanceData = await handleSaveRemittance(remittanceForm.getFieldsValue(), remittanceForm, null)

    if (remittanceData) {
      dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.PAYMENT_REMITTANCE}_new`,
          closable: true,
          label: 'Create New Medical Aid Payment',
          children: <MedicalAidPaymentForm data={remittanceData} handleGetRemittances={handleGetRemittances} />
        })
      )
    }
  }

  // Button style for actions
  const buttonStyle = {
    height: '40px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '0 16px',
    fontWeight: 500,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  }

  // Generate skeleton data for table
  const generateSkeletonData = () => {
    return Array(10)
      .fill()
      .map((_, index) => ({
        key: `skeleton-${index}`,
        _id: `skeleton-${index}`,
        skeleton: true,
        createdAt: <Skeleton.Input active size='small' style={{ width: '90%' }} />,
        medicalAid: <Skeleton.Input active size='small' style={{ width: '100%' }} />,
        receiptNumber: <Skeleton.Input active size='small' style={{ width: '90%' }} />,
        total: <Skeleton.Input active size='small' style={{ width: '90%' }} />,
        runningTotal: <Skeleton.Input active size='small' style={{ width: '90%' }} />,
        finalizedDate: <Skeleton.Input active size='small' style={{ width: '90%' }} />,
        finalized: (
          <div className='skeleton-tag'>
            <Skeleton.Input active size='small' style={{ width: '80%', borderRadius: '16px' }} />
          </div>
        ),
        actions: (
          <div className='skeleton-action'>
            <Skeleton.Button active size='small' style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
            <Skeleton.Button active size='small' style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
          </div>
        )
      }))
  }

  return (
    <ContainerCard bodyStyle={{ padding: 0 }}>
      <style>{spinAnimation}</style>
      <PageHeader title='Remittances Management' icon={faFileInvoiceDollar} />

      <div style={{ padding: '16px 24px' }}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Row gutter={[0, 0]} align='middle' justify='space-between' wrap>
              <Col xs={24} sm={14} md={16} lg={16} xl={16}>
                <Space wrap>
                  <Input
                    placeholder='Search remittances...'
                    prefix={
                      <span
                        style={{
                          background: token.actionColor || token.colorPrimary,
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '8px'
                        }}
                      >
                        <FontAwesomeIcon icon={faSearch} style={{ color: 'white', fontSize: '12px' }} />
                      </span>
                    }
                    allowClear
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    style={{
                      width: '300px',
                      maxWidth: '100%',
                      borderRadius: '6px',
                      height: '40px'
                    }}
                  />
                  <DatePicker
                    placeholder='Date'
                    style={{
                      width: '150px',
                      borderRadius: '6px',
                      height: '40px'
                    }}
                    value={filters.createdAt ? dayjs(filters.createdAt) : null}
                    onChange={(date) => {
                      setFilters({ ...filters, createdAt: date })
                    }}
                    allowClear
                  />
                </Space>
              </Col>
              <Col xs={24} sm={10} md={8} lg={8} xl={8}>
                <Row gutter={[12, 8]} justify='end'>
                  <Col>
                    <Tooltip title='Refresh remittance list'>
                      <Button
                        icon={
                          <FontAwesomeIcon
                            icon={faRefresh}
                            style={{
                              color: token.actionColor || token.colorPrimary,
                              animation: loading ? 'spin 1s linear infinite' : 'none',
                              marginRight: 8
                            }}
                          />
                        }
                        onClick={handleGetRemittances}
                        disabled={loading}
                        type='default'
                        size='large'
                        style={{
                          ...buttonStyle,
                          background: 'rgba(24, 144, 255, 0.1)',
                          borderColor: 'rgba(24, 144, 255, 0.3)',
                          color: token.actionColor || token.colorPrimary
                        }}
                      >
                        Refresh
                      </Button>
                    </Tooltip>
                  </Col>

                  <Col>
                    <Button
                      type='primary'
                      style={{
                        ...buttonStyle,
                        background: token.actionColor || token.colorPrimary,
                        borderColor: token.actionColor || token.colorPrimary
                      }}
                      onClick={() => setRemittanceModalVisible(true)}
                      icon={<FontAwesomeIcon icon={faAdd} />}
                      size='large'
                    >
                      New Remittance
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Table
              loading={false}
              dataSource={loading ? generateSkeletonData() : data}
              bordered
              size='small'
              rowKey='_id'
              columns={[
                {
                  title: <h2>Creation Date</h2>,
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: (text, record) => {
                    if (record.skeleton) return text
                    return dayjs(text).format('DD-MM-YYYY')
                  },
                  sorter: loading ? false : (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
                  width: '10%',
                  className: (record) => (record.skeleton ? 'skeleton-cell' : '')
                },
                {
                  title: <h2>Medical Aid Scheme</h2>,
                  dataIndex: 'medicalAid',
                  key: 'medicalAid',
                  width: '15%',
                  render: (text, record) => {
                    if (record.skeleton) return text
                    if (!text) return '-'
                    // Convert to title case (capitalize first letter of each word)
                    return text
                      .split(' ')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ')
                  },
                  sorter: loading ? false : (a, b) => (a.medicalAid || '').localeCompare(b.medicalAid || ''),
                  className: (record) => (record.skeleton ? 'skeleton-cell' : '')
                },
                {
                  title: <h2>Receipt Number</h2>,
                  dataIndex: 'receiptNumber',
                  key: 'receiptNumber',
                  sorter: loading ? false : (a, b) => (a.receiptNumber || '').localeCompare(b.receiptNumber || ''),
                  className: (record) => (record.skeleton ? 'skeleton-cell' : '')
                },
                {
                  title: <h2>Total</h2>,
                  dataIndex: 'total',
                  key: 'total',
                  render: (text, record) => {
                    if (record.skeleton) return text
                    return `R ${parseFloat(text || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}`
                  },
                  sorter: loading ? false : (a, b) => parseFloat(a.total || 0) - parseFloat(b.total || 0),
                  className: (record) => (record.skeleton ? 'skeleton-cell' : '')
                },
                {
                  title: <h2>Outstanding</h2>,
                  dataIndex: 'runningTotal',
                  key: 'runningTotal',
                  render: (text, record) => {
                    if (record.skeleton) return text
                    return `R ${parseFloat(text || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}`
                  },
                  sorter: loading ? false : (a, b) => parseFloat(a.runningTotal || 0) - parseFloat(b.runningTotal || 0),
                  className: (record) => (record.skeleton ? 'skeleton-cell' : '')
                },
                {
                  title: <h2>Finalized Date</h2>,
                  dataIndex: 'finalizedDate',
                  key: 'finalizedDate',
                  render: (text, record) => {
                    if (record.skeleton) return text
                    return text ? dayjs(text).format('DD-MM-YYYY') : '-'
                  },
                  className: (record) => (record.skeleton ? 'skeleton-cell' : '')
                },
                {
                  title: <h2>Finalized</h2>,
                  dataIndex: 'finalized',
                  key: 'finalized',
                  render: (text, record) => {
                    if (record.skeleton) return text
                    return text ? (
                      <center>
                        <CheckCircleOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                      </center>
                    ) : (
                      <center>
                        <CloseCircleOutlined style={{ fontSize: 20, color: token.colorError }} />
                      </center>
                    )
                  },
                  width: '10%',
                  className: (record) => (record.skeleton ? 'skeleton-cell' : '')
                },
                {
                  title: <h2>Actions</h2>,
                  key: 'actions',
                  width: '10%',
                  align: 'center',
                  fixed: 'right',
                  render: (text, record) => {
                    if (record.skeleton) return record.actions

                    return (
                      <Space size={8} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title='View Remittance'>
                          <Button
                            type='primary'
                            size='small'
                            onClick={() => handleOpenRemittance(record)}
                            icon={<FontAwesomeIcon icon={faEye} />}
                            style={{
                              height: '32px',
                              width: '32px',
                              minWidth: '32px',
                              background: token.actionColor || token.colorPrimary,
                              borderRadius: '50%',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                              border: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}
                          />
                        </Tooltip>
                        <Tooltip title={record.finalized ? 'Already Finalized' : 'Finalize Remittance'}>
                          <Button
                            type='primary'
                            size='small'
                            onClick={() => !record.finalized && handleFinalizeRemittance(record)}
                            icon={<FontAwesomeIcon icon={faCheckCircle} />}
                            disabled={record.finalized}
                            style={{
                              height: '32px',
                              width: '32px',
                              minWidth: '32px',
                              background: record.finalized ? '#f0f0f0' : '#52c41a',
                              borderRadius: '50%',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                              border: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              opacity: record.finalized ? 0.5 : 1,
                              cursor: record.finalized ? 'not-allowed' : 'pointer'
                            }}
                          />
                        </Tooltip>
                      </Space>
                    )
                  },
                  className: (record) => (record.skeleton ? 'skeleton-cell' : '')
                }
              ]}
              pagination={{
                total: total,
                current: page,
                pageSize: pageSize,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} remittances`,
                position: ['bottomCenter'],
                pageSizeOptions: ['10', '20', '50', '100'],
                style: { marginTop: 16 }
              }}
              onChange={(pagination, filters, sorter) => {
                if (!loading) {
                  setPage(pagination.current)
                  setPageSize(pagination.pageSize)
                }
              }}
              scroll={{ x: 1200 }}
              style={{
                borderRadius: 8,
                overflow: 'hidden'
              }}
            />
          </Col>
        </Row>
      </div>

      <Modal
        title='Create Payment Remittance'
        closable={false}
        destroyOnClose={true}
        okText='Save'
        cancelButtonProps={{ danger: true }}
        okButtonProps={{ loading: remittanceLoading }}
        onCancel={() => {
          remittanceForm.resetFields()
          setRemittanceModalVisible(false)
        }}
        open={remittanceModalVisible}
        onOk={handleSaveAndOpenPaymentForm}
        width={800}
      >
        <Form
          layout='vertical'
          form={remittanceForm}
          initialValues={{
            receiptNumber: dayjs(new Date()).format('YYMMDDHHmmss')
          }}
        >
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <Form.Item
                name='medicalAid'
                label='Medical Aid'
                rules={[{ required: true, message: 'Medical Aid is required' }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder='Search for a Medical Aid...'
                  onSearch={(value) => {
                    debouncedSchemeFilter(value)
                  }}
                  onChange={(e, option) => {
                    remittanceForm.setFieldValue('medicalAid', option?.data ? JSON.parse(option.data).name : '')
                    setSchemeData([])
                  }}
                  options={schemeData.map((i) => {
                    return {
                      label: i.name,
                      value: i.name,
                      data: JSON.stringify(i)
                    }
                  })}
                  notFoundContent={
                    schemeLoading ? <AgiliteSkeleton skActive spinnerTip='Searching Schemes...' /> : undefined
                  }
                />
              </Form.Item>
              <Form.Item
                name='remittanceDate'
                label='Remittance Date'
                rules={[{ required: true, message: 'Remittance Date is required' }]}
              >
                <DatePicker style={{ width: '100%' }} placeholder='Remittance Date' />
              </Form.Item>
              <Form.Item
                name='receiptNumber'
                label='Receipt Number'
                rules={[{ required: true, message: 'Receipt Number is required' }]}
              >
                <Input readOnly style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='total' label='Total' rules={[{ required: true, message: 'Total is required' }]}>
                <Input
                  addonBefore='R'
                  style={{ width: '100%' }}
                  onChange={(e) => {
                    remittanceForm.setFieldValue('runningTotal', e.target.value)
                  }}
                />
              </Form.Item>
              <Form.Item
                name='runningTotal'
                label='Running Total'
                rules={[{ required: true, message: 'Running Total is required' }]}
              >
                <Input addonBefore='R' style={{ width: '100%' }} disabled />
              </Form.Item>
              <Form.Item name='practiceRefNo' label='Practice Reference Number'>
                <Input style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </ContainerCard>
  )
}

export default RemittanceList
